import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { BrandedLayout } from '@/components/BrandedLayout';

/**
 * Reset-password page
 *
 * User lands here from the password-reset email. Supabase Auth has already
 * exchanged the recovery link for a temporary session, so calling
 * supabase.auth.updateUser() will succeed.
 *
 * After a successful reset, sign the user out and redirect to /auth/sign-in
 * with a success message in route state — SignIn renders that as a green
 * banner above the form, so the user has a confirmation that the change
 * landed and the next step is clear.
 */
export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasRecoverySession, setHasRecoverySession] = useState<boolean | null>(
    null
  );

  // On mount, verify we have a Supabase session from the recovery link.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      setHasRecoverySession(!!data.session);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function validate(): string | null {
    if (password.length < 12) return 'Password must be at least 12 characters.';
    if (!/[a-z]/.test(password) || !/[A-Z]/.test(password) || !/\d/.test(password))
      return 'Password must include a lowercase letter, an uppercase letter, and a digit.';
    if (password !== confirm) return 'Passwords do not match.';
    return null;
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    const v = validate();
    if (v) {
      setError(v);
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        setError(error.message);
        return;
      }
      await supabase.auth.signOut();
      // Hard reload so the identity store fully resets — soft navigate raced
      // with the in-flight TOKEN_REFRESHED from updateUser and briefly left
      // the user signed in, which then bounced GuestOnly to /. Carry the
      // success message via sessionStorage since route state doesn't survive
      // a full page reload.
      try { sessionStorage.setItem('auth_flash', 'Password updated. Sign in with your new password.'); } catch { /* ignore */ }
      window.location.replace('/auth/sign-in');
    } finally {
      setSubmitting(false);
    }
  }

  if (hasRecoverySession === null) {
    return (
      <BrandedLayout>
        <div className="container mx-auto py-16 px-4 text-center text-sm text-muted-foreground">
          Loading...
        </div>
      </BrandedLayout>
    );
  }

  if (!hasRecoverySession) {
    return (
      <BrandedLayout>
        <div className="container mx-auto py-16 px-4">
          <div className="mx-auto w-full max-w-sm space-y-4 text-center">
            <h1 className="text-xl font-semibold tracking-tight">
              Reset link expired or invalid
            </h1>
            <p className="text-sm text-muted-foreground">
              Reset links expire after 1 hour and can be used only once. Request
              a new one to continue.
            </p>
            <Link
              to="/auth/forgot-password"
              className="inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
            >
              Request a new reset link
            </Link>
          </div>
        </div>
      </BrandedLayout>
    );
  }

  return (
    <BrandedLayout>
      <div className="container mx-auto py-12 px-4">
        <div className="mx-auto w-full max-w-sm space-y-6">
          <div className="text-center space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">
              Set a new password
            </h1>
            <p className="text-sm text-muted-foreground">
              Min 12 characters; upper, lower, and a digit.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1">
              <label htmlFor="password" className="block text-sm font-medium">
                New password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  minLength={12}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputClass + ' pr-20'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 px-3 text-xs font-medium text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  tabIndex={-1}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div className="space-y-1">
              <label htmlFor="confirm" className="block text-sm font-medium">
                Confirm new password
              </label>
              <div className="relative">
                <input
                  id="confirm"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  minLength={12}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className={inputClass + ' pr-20' + (confirm && password !== confirm ? ' border-destructive focus-visible:ring-destructive' : '')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 px-3 text-xs font-medium text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  tabIndex={-1}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              {confirm && password !== confirm && (
                <p className="text-xs text-destructive" role="alert">
                  Passwords don&rsquo;t match yet.
                </p>
              )}
            </div>

            {error && (
              <p
                role="alert"
                className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {submitting ? 'Updating...' : 'Update password'}
            </button>
          </form>
        </div>
      </div>
    </BrandedLayout>
  );
}

const inputClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';
